import React, { useState } from 'react';
import { X, Download, Maximize2, Minimize2, FileText, Image, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentViewer = ({ document, token, onClose }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [pdfError, setPdfError] = useState(false);

  if (!document) return null;

  const isPDF = document.mime_type === 'application/pdf';
  const isImage = document.mime_type?.startsWith('image/');
  const fileUrl = `${API_URL}${document.file_url}`;

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = document.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleZoomIn = () => setImageZoom(Math.min(imageZoom + 0.25, 3));
  const handleZoomOut = () => setImageZoom(Math.max(imageZoom - 0.25, 0.5));
  const handleRotate = () => setImageRotation((imageRotation + 90) % 360);

  return (
    <div 
      className={`fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 ${
        isFullscreen ? 'p-0' : 'p-4 md:p-8'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white flex flex-col overflow-hidden ${
          isFullscreen 
            ? 'w-full h-full rounded-none' 
            : 'w-full max-w-5xl h-[90vh] rounded-2xl'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${isPDF ? 'bg-red-100' : 'bg-blue-100'}`}>
              {isPDF ? (
                <FileText className="w-5 h-5 text-red-600" />
              ) : (
                <Image className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-800 truncate">{document.title}</h3>
              <p className="text-sm text-gray-500 truncate">{document.file_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Image controls */}
            {isImage && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Zoom arrière"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500 min-w-[3rem] text-center">
                  {Math.round(imageZoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Zoom avant"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Pivoter"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-gray-200 mx-1" />
              </>
            )}
            
            {/* Download */}
            <Button
              onClick={handleDownload}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Télécharger</span>
            </Button>
            
            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5" />
              ) : (
                <Maximize2 className="w-5 h-5" />
              )}
            </button>
            
            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg"
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          {isPDF ? (
            pdfError ? (
              <div className="text-center p-8">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">
                  Impossible d'afficher le PDF dans le navigateur
                </p>
                <Button onClick={handleDownload} className="bg-green-500 hover:bg-green-600 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le PDF
                </Button>
              </div>
            ) : (
              <iframe
                src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full border-0"
                title={document.title}
                onError={() => setPdfError(true)}
              />
            )
          ) : isImage ? (
            <div 
              className="flex items-center justify-center p-4 min-h-full overflow-auto"
              style={{ cursor: imageZoom > 1 ? 'grab' : 'default' }}
            >
              <img
                src={fileUrl}
                alt={document.title}
                className="max-w-none transition-transform duration-200"
                style={{
                  transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                  maxHeight: imageZoom === 1 ? '100%' : 'none',
                  maxWidth: imageZoom === 1 ? '100%' : 'none'
                }}
              />
            </div>
          ) : (
            <div className="text-center p-8">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">
                Ce type de fichier ne peut pas être prévisualisé
              </p>
              <Button onClick={handleDownload} className="bg-green-500 hover:bg-green-600 text-white">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le fichier
              </Button>
            </div>
          )}
        </div>

        {/* Footer with document info */}
        {document.description && (
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Description :</span> {document.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
